import get from "~/libs/get";
import isMatchesQualification from "../is-matches-qualification";

/**
 * @typedef {import("../is-matches-qualification").QualificationDefinition} QualificationDefinition
 * @typedef {Object} IgnoreDefinition An ignore object defining when an error should be ignored based on the QualificationDefinitions
 * @property {{[field: string]: QualificationDefinition}} [Ignore.payload]
 * @property {QualificationDefinition} [Ignore.args]
 * @property {boolean} [Ignore.isUncaught]
 * @property {QualificationDefinition} [Ignore.message]
 * @property {QualificationDefinition} [Ignore.route]
 * @property {QualificationDefinition} [Ignore.stack]
 */

/**
 * A checkIgnore function that returns true when an {@link IgnoreDefinition} matches the error
 * @param {object[]} ignoreList
 * @param nuxtApp
 * @returns {function(boolean, any[], any): boolean}
 */
export default function checkIgnore(ignoreList = [], nuxtApp) {
  return function (isUncaught, args, payload) {
    // if there's nothing to ignore, never ignore
    if (ignoreList.length === 0) return false
    const supportedKeys = ['payload', 'args', 'message', 'isUncaught', 'route', 'stack']
    const results = ignoreList.map((ignored) => {
      if (!Object.keys(ignored).some((key) => supportedKeys.includes(key))) {
        return false
      }
      if ("payload" in ignored) {
        for (const [path, option] of Object.entries(ignored.payload)) {
          const subject = get(payload, path)
          for (const [qualifier, qualification] of Object.entries(option)) {
            if (!isMatchesQualification(qualifier, qualification, subject)) return false
          }
        }
      }
      if ("args" in ignored) {
        for (const [qualifier, qualification] of Object.entries(ignored.args)) {
          if (!isMatchesQualification(qualifier, qualification, args)) return false
        }
      }
      if ("message" in ignored) {
        let message
        if (args?.length >= 2 && args[1] && typeof args[1] === 'object') {
          message = args[1].message // could be undefined
        }
        if (message === undefined) { // if our first if was false or if the object didn't contain the message
          const typeofArgs0 = args[0] && typeof args[0] // sets to type if args[0] is not null, since null is type object
          if (typeofArgs0 === 'string') {
            message = args[0]
          } else if (typeofArgs0 === 'object') {
            message = args[0].message // could still be undefined, depending on context
          }
        }
        if (message === undefined) {
          let exception
          if (payload?.body?.trace) {
            exception = payload.body.trace.exception
          } else {
            const traceChain = payload?.body?.trace_chain ?? []
            for (const trace of traceChain) {
              if (trace?.exception) {
                exception = trace.exception
                break
              }
            }
          }
          message = exception?.message
        }
        for (const [qualifier, qualification] of Object.entries(ignored.message)) {
          if (!isMatchesQualification(qualifier, qualification, message)) return false
        }
      }
      if ("isUncaught" in ignored) {
        if (ignored.isUncaught !== isUncaught) return false
      }
      if ("route" in ignored) {
        let route
        if (nuxtApp) {
          route = nuxtApp.$router?.currentRoute?.value?.fullPath
        }
        route ??= window?.location?.href
        for (const [qualifier, qualification] of Object.entries(ignored.route)) {
          if (!isMatchesQualification(qualifier, qualification, route)) return false
        }
      }
      if ("stack" in ignored) {
        // Prefer the thrown Error's own stack; fall back to the payload's frame
        let stack
        if (args?.length >= 2 && args[1] && typeof args[1] === 'object') {
          stack = args[1].stack
        }
        if (!stack && args?.[0] && typeof args[0] === 'object') {
          stack = args[0].stack
        }
        if (!stack) {
          const traces = payload?.body?.trace
            ? [payload.body.trace]
            : (payload?.body?.trace_chain ?? [])
          stack = traces
            .flatMap((trace) => trace?.frames ?? [])
            .map((frame) => frame?.filename)
            .filter(Boolean)
            .join('\n')
        }
        for (const [qualifier, qualification] of Object.entries(ignored.stack)) {
          if (!isMatchesQualification(qualifier, qualification, stack)) return false
        }
      }

      // if there were things to ignore, and all qualifications matched, ignore the error
      return true
    })

    return results.includes(true)
  }
}
